import React, { useState, useEffect } from "react";
import { DatePickerIcon, TooltipsIcon } from "../../assets/icons";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { reqToGetGeneralOptions } from "../../redux/slices.js/settings/generalOptionsSlice";
import { reqToUpdateSettings } from "../../redux/slices.js/settings/settingsSlice";

const timeFormatOptions = [
  { value: "12 hour", label: "12 hour" },
  { value: "24 hour", label: "24 hour" },
];

const attemptsOptions = [
  { value: "3", label: "3" },
  { value: "5", label: "5" },
  { value: "7", label: "7" },
  { value: "9", label: "9" },
];

const sessionTimeoutOptions = [
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "5", label: "5" },
  { value: "7", label: "7" },
  { value: "9", label: "9" },
];

const General = ({ settings = [] }) => {
  const dispatch = useDispatch();
  const {
    timezones = [],
    currencies = [],
    languages = [],
  } = useSelector((state) => state.generalSettings || {});

  // Convert settings array → object { name: value }
  const initialSettings = settings.reduce((acc, s) => {
    acc[s.name] = s.value;
    return acc;
  }, {});

  const [formValues, setFormValues] = useState({
    system_name: initialSettings.system_name || "",
    default_timezone: initialSettings.default_timezone || "",
    start_date: new Date(), // fallback
    date_format: initialSettings.date_format || "MM/DD/YYYY",
    time_format: initialSettings.time_format || "",
    default_currency: initialSettings.default_currency || "",
    default_language: initialSettings.default_language || "English",
    max_login_attempts: initialSettings.max_login_attempts || "",
    session_timeout_minutes: initialSettings.session_timeout_minutes || "",
  });

  // Fetch dynamic options on mount
  useEffect(() => {
    dispatch(reqToGetGeneralOptions({ name: "timezones" }));
    dispatch(reqToGetGeneralOptions({ name: "currencies" }));
    dispatch(reqToGetGeneralOptions({ name: "languages" }));
  }, [dispatch]);

  // Generic update handler
  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveChanges = () => {
    const payload = Object.entries(formValues).map(([name, value]) => ({
      name,
      value: value instanceof Date ? value.toISOString() : value,
    }));

    dispatch(
      reqToUpdateSettings({
        data: payload,
        onSuccess: (response) => {
          // any additional success logic here
        },
        onFailure: (error) => {
          console.error("Failed to update settings:", error);
          // any additional failure logic here
        },
      })
    );
  };

  return (
    <>
      <div className="grid gap-4 md:gap-6 md:grid-cols-2">
        {/* System Name */}
        <div className="form-group">
          <label>
            System Name <span>*</span>
          </label>
          <input
            type="text"
            placeholder="Enter company name"
            value={formValues.system_name}
            onChange={(e) => handleChange("system_name", e.target.value)}
          />
        </div>

        {/* Default Timezone */}
        <div className="form-group">
          <label>
            Default timezone <span>*</span>
          </label>
          <Select
            options={timezones}
            value={timezones.find(
              (t) => t.value === formValues.default_timezone
            )}
            onChange={(val) => handleChange("default_timezone", val.value)}
            isClearable={false}
            isSearchable={false}
          />
        </div>

        {/* Start Date */}
        <div className="form-group">
          <label>
            Start Date <span>*</span>
          </label>
          <div className="relative">
            <DatePicker
              selected={formValues.start_date}
              onChange={(date) => handleChange("start_date", date)}
              dateFormat="MM/dd/yyyy"
              className="pe-5"
            />
            <span className="absolute right-3 top-3 h-5 w-5 text-gray-500 pointer-events-none">
              {DatePickerIcon}
            </span>
          </div>
        </div>

        {/* Time Format */}
        <div className="form-group">
          <label>
            Time Format <span>*</span>
          </label>
          <Select
            options={timeFormatOptions}
            value={timeFormatOptions.find(
              (t) => t.value === formValues.time_format
            )}
            onChange={(val) => handleChange("time_format", val.value)}
            isClearable={false}
            isSearchable={false}
          />
        </div>

        {/* Default Currency */}
        <div className="form-group md:col-span-2">
          <label>
            Default Currency <span>*</span>
          </label>
          <Select
            options={currencies}
            value={currencies.find((c) =>
              c.value.startsWith(formValues.default_currency)
            )}
            onChange={(val) =>
              handleChange("default_currency", val.value.split(" ")[0])
            }
            isClearable={false}
            isSearchable={false}
          />
        </div>

        {/* Default Language */}
        <div className="form-group">
          <label>
            Default Language <span>*</span>
          </label>
          <Select
            options={languages}
            value={languages.find(
              (l) => l.value === formValues.default_language
            )}
            onChange={(val) => handleChange("default_language", val.value)}
            isClearable={false}
            isSearchable={true}
          />
        </div>

        {/* Max Login Attempts */}
        <div className="form-group">
          <label>
            Max Login Attempts <span>*</span>
          </label>
          <Select
            options={attemptsOptions}
            value={attemptsOptions.find(
              (a) => a.value === formValues.max_login_attempts
            )}
            onChange={(val) => handleChange("max_login_attempts", val.value)}
            isClearable={false}
            isSearchable={false}
          />
        </div>

        {/* Session Timeout */}
        <div className="form-group md:col-span-2">
          <label>
            Session Timeout (minutes) <span>*</span>
          </label>
          <Select
            options={sessionTimeoutOptions}
            value={sessionTimeoutOptions.find(
              (s) => s.value === formValues.session_timeout_minutes
            )}
            onChange={(val) =>
              handleChange("session_timeout_minutes", val.value)
            }
            isClearable={false}
            isSearchable={false}
          />
        </div>
      </div>

      {/* Save Changes */}
      <div className="mt-auto flex gap-3 pt-6 justify-end flex-wrap">
        <button type="button" className="next-btn" onClick={handleSaveChanges}>
          Save Changes
        </button>
      </div>
    </>
  );
};

export default General;
